/**
 * Load Data Pipeline → Market Intel Consumer.
 *
 * The consumer's output, and the two design claims worth auditing visually:
 *
 *   1. SEVERAL INSTANCES WRITE CONCURRENTLY. writer_id is part of the primary
 *      key so each instance replaces only its own rows. More than one writer
 *      here, with none starving, is that working — it cannot be shown by a
 *      test, only by production.
 *   2. RATES ARE STORED AS A HISTOGRAM, NOT AN AVERAGE. Averages compose;
 *      percentiles do not. Fixed $0.25 buckets make a percentile over any
 *      window a walk over summed counts.
 */

import Box from '@mui/joy/Box'
import Card from '@mui/joy/Card'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import * as React from 'react'
import { loadPipelineApi } from 'api/loadPipeline'
import { ErrorState, Loading, Stat, StatRow, WindowPicker, useLoadPipelineData, formatLag, formatNumber } from './components/shared'

export default function LoadPipelineConsumerPage() {
    const [hours, setHours] = React.useState(24)
    const { data, error, loading } = useLoadPipelineData(() => loadPipelineApi.getConsumer(hours), [hours])
    const refusals = useLoadPipelineData(() => loadPipelineApi.getConsumerRefusals(hours), [hours])

    const lag = formatLag(data?.summary.lagSeconds ?? null)
    const peakBucket = Math.max(1, ...(data?.distribution ?? []).map((b) => b.count))
    const totalObs = (data?.distribution ?? []).reduce((s, b) => s + b.count, 0)

    /** Percentile from summed bucket counts — the whole point of storing counts. */
    const percentile = React.useCallback(
        (p: number): string => {
            const buckets = data?.distribution ?? []
            if (totalObs === 0) return '—'
            const target = totalObs * p
            let seen = 0
            for (const b of buckets) {
                seen += b.count
                if (seen >= target) return `$${(b.lowerEdge + (data?.bucketWidth ?? 0.25) / 2).toFixed(2)}`
            }
            return '—'
        },
        [data, totalObs]
    )

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', mb: 2 }}>
                <WindowPicker hours={hours} onChange={setHours} maxHours={168} />
            </Box>

            {error ? <ErrorState error={error} /> : null}
            {loading && !data ? <Loading /> : null}

            {data ? (
                <>
                    <StatRow>
                        <Stat label="Observations" value={formatNumber(data.summary.observations)} />
                        <Stat label="Median $/mi" value={percentile(0.5)} hint="from summed histogram buckets" />
                        <Stat label="p25 – p75" value={`${percentile(0.25)} – ${percentile(0.75)}`} hint="interquartile range" />
                        <Stat label="Instances" value={formatNumber(data.writers.length)} hint="writing concurrently" />
                        <Stat label="Freshness" value={lag.text} tone={lag.tone} />
                    </StatRow>

                    <Card sx={{ mb: 3 }}>
                        <Typography level="title-md">Rate distribution</Typography>
                        <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 2 }}>
                            ${data.bucketWidth.toFixed(2)}-wide buckets. Stored as counts per bucket, so any window&apos;s percentile is a walk over sums —
                            which is not possible from stored averages.
                        </Typography>
                        {data.distribution.length === 0 ? (
                            <Typography level="body-sm" textColor="text.tertiary">
                                No observations in this window.
                            </Typography>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, height: 160 }}>
                                {data.distribution.map((b) => (
                                    <Box
                                        key={b.lowerEdge}
                                        title={`$${b.lowerEdge.toFixed(2)}–$${(b.lowerEdge + data.bucketWidth).toFixed(2)} — ${formatNumber(b.count)}`}
                                        sx={{
                                            flex: 1,
                                            minWidth: 3,
                                            height: `${Math.max(2, (b.count / peakBucket) * 100)}%`,
                                            bgcolor: 'success.solidBg',
                                            borderRadius: 2,
                                        }}
                                    />
                                ))}
                            </Box>
                        )}
                    </Card>

                    <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2 }}>
                        <Typography level="title-md" sx={{ mb: 0.5 }}>
                            Writer instances
                        </Typography>
                        <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                            Each instance owns its rows via <code>writer_id</code> in the primary key, so concurrent flushes cannot overwrite each other.
                        </Typography>
                        <Table size="sm">
                            <thead>
                                <tr>
                                    <th>Writer</th>
                                    <th style={{ textAlign: 'right' }}>Cells</th>
                                    <th style={{ textAlign: 'right' }}>Observations</th>
                                    <th style={{ textAlign: 'right' }}>Last write</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.writers.map((w) => (
                                    <tr key={w.writerId}>
                                        <td>
                                            <code>{w.writerId}</code>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{formatNumber(w.cells)}</td>
                                        <td style={{ textAlign: 'right' }}>{formatNumber(w.observations)}</td>
                                        <td style={{ textAlign: 'right' }}>{new Date(w.lastSeen).toLocaleString()}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </Sheet>
                </>
            ) : null}
            {refusals.data ? (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', p: 2, mt: 3 }}>
                    <Typography level="title-md" sx={{ mb: 0.5 }}>
                        Refused updates
                    </Typography>
                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mb: 1 }}>
                        Updates a consumer could not parse as a load update. Each was recorded and acknowledged, never trusted.
                    </Typography>
                    {refusals.data.sample.length === 0 ? (
                        <Typography level="body-sm" textColor="text.tertiary">
                            Nothing refused in this window.
                        </Typography>
                    ) : (
                        <>
                            <Table size="sm" sx={{ mb: 2 }}>
                                <thead>
                                    <tr>
                                        <th>Consumer</th>
                                        <th>Failure</th>
                                        <th style={{ textAlign: 'right' }}>Refused</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {refusals.data.totals.map((t) => (
                                        <tr key={`${t.consumer}|${t.parse}`}>
                                            <td>
                                                <code>{t.consumer}</code>
                                            </td>
                                            <td>
                                                <code>{t.parse}</code>
                                            </td>
                                            <td style={{ textAlign: 'right' }}>{formatNumber(t.count)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                            <Table size="sm">
                                <thead>
                                    <tr>
                                        <th>When</th>
                                        <th>Consumer</th>
                                        <th>Source</th>
                                        <th>Detail</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {refusals.data.sample.map((r) => (
                                        <tr key={r.id}>
                                            <td>{r.quarantinedAt ? new Date(r.quarantinedAt).toLocaleString() : '—'}</td>
                                            <td>
                                                <code>{r.consumer}</code>
                                            </td>
                                            <td>{r.source ?? '—'}</td>
                                            <td>{r.detail}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </>
                    )}
                </Sheet>
            ) : null}
        </Box>
    )
}
